import { useContext, useState } from 'react';
import {
  SrpFilterApiParams,
  transformSrpFilterApiParamsToState,
  transformSrpFilterStateToApiParams,
} from 'lib/srpFilterTransformers';
import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import eventLogger from 'lib/EventLogger';
import { SrpPageViewEvent } from 'lib/Tracking/Srp/PageViewEvent';
import SearchCreateEvent from 'lib/Tracking/Srp/SearchCreateEvent';
import { AdGlobalRefreshContext } from 'contexts/AdGlobalRefreshContext';

import { SearchV2ApiLocationParams } from 'types/Apis/SearchV2LocationParams';
import filterSearchParams from 'lib/filterSearchParams';
import { SearchParams } from 'lib/apis/SearchApi/types';
import { SearchResult } from 'lib/apis/SearchApi';
import { useTrackedEffect } from './useTrackedEffect';
import { useSrpFilters } from './useSrpFilters';
import useSearchVehicles from './useSearchVehicles';
import useHashLocation from './useHashLocation';
import useShouldSrpLoad from './useShouldSrpLoad';
import useDeviceScreen from './useDeviceScreen';
import useSrpMobileSidebar from './useSrpMobileSidebar';

export type InitialSrpParams = Omit<SearchParams, 'locale' | 'latitude' | 'longitude'> &
  SearchV2ApiLocationParams;

export interface UseSrpModelProps {
  initialParams?: InitialSrpParams;
}

export interface UseSrpModelResult {
  data: SearchResult;
  isPending: boolean;
  hasFetchedOnce: boolean;
  error: unknown;
  searchVehicles: (sortBy?: string) => Promise<void>;
}

export type { SrpFilterApiParams };

export default function useSrpModel({ initialParams }: UseSrpModelProps): UseSrpModelResult {
  const { values, setValues } = useSrpFilters();
  const apiParams = transformSrpFilterStateToApiParams(values);
  const hashLocation = useHashLocation();
  const { isMobile } = useDeviceScreen();
  const { isOpen: isMobileSidebarOpen } = useSrpMobileSidebar();
  const [searchVehicles, { data, isPending, error, hasFetchedOnce }] = useSearchVehicles();
  const { refreshGlobalAds } = useContext(AdGlobalRefreshContext);
  const [currentPage, setCurrentPage] = useState(initialParams?.page);

  // use stringified values to avoid triggering effect when values change by-reference-only
  const apiParamsStr = JSON.stringify(apiParams);
  const hashLocationStr = JSON.stringify(hashLocation.values);

  // shouldLoad = filter state initialized + geolocation completed (ie. lat/lon set)
  const shouldLoad = useShouldSrpLoad({ initialParams });

  function fetchVehicles(params: InitialSrpParams | SrpFilterApiParams | undefined): void {
    /* If search params have changed but the page hasn't, log the search_create event */
    if (params?.page === currentPage) {
      eventLogger.log(SearchCreateEvent(params));
    }
    setCurrentPage(params?.page);
    /* 
      If both latitude and longitude aren't provided in params, omit both from initialParams 
      We do this to ensure SearchV2 API call fails lat/lon validation and redirects when appropriate
    */
    let coordinateCount = 0;
    let searchParams = {
      ...initialParams,
      ...params,
      ...(isMobile && isMobileSidebarOpen && { limit: 0 }),
    };
    if (params?.latitude) coordinateCount += 1;
    if (params?.longitude) coordinateCount += 1;
    if (coordinateCount < 2) {
      searchParams = {
        ...Object.fromEntries(
          Object.entries(initialParams || {}).filter(
            ([key]) => !['latitude', 'longitude'].includes(key)
          )
        ),
        ...params,
        ...(isMobile && isMobileSidebarOpen && { limit: 0 }),
      };
    }
    searchVehicles(filterSearchParams(searchParams));
  }

  useTrackedEffect(
    ([apiParamsStrChanged, hashLocationStrChanged, shouldLoadChanged], initialRender) => {
      const isFirstSrpRender = initialRender && !shouldLoad;
      // Update url with either default params on initial srp load or filter changes
      if (isFirstSrpRender || apiParamsStrChanged) {
        hashLocation.navigate({ ...hashLocation.values, ...apiParams });

        if (shouldLoad) {
          refreshGlobalAds();
          eventLogger.log(SrpPageViewEvent(window?.location?.href));
        }
        // Fetch results when either url changes or page is initialized and ready
      } else if (hashLocationStrChanged || shouldLoadChanged) {
        if (!shouldLoad || hashLocation.values.vin) return;

        setValues({
          ...srpFilterInitialValues,
          ...transformSrpFilterApiParamsToState(hashLocation.values),
        });
        fetchVehicles(hashLocation.values);
        // Fetch results when coming back from on-page vdp
      } else if (!hasFetchedOnce) {
        if (!shouldLoad) return;
        fetchVehicles(hashLocation.values);
      }
    },
    [apiParamsStr, hashLocationStr, shouldLoad, hasFetchedOnce]
  );

  function onSearchVehicles(): Promise<void> {
    return searchVehicles(filterSearchParams({ ...initialParams, ...apiParams }));
  }

  return { data, isPending, hasFetchedOnce, error, searchVehicles: onSearchVehicles };
}
